import {
  clearChatState,
  getAvatarToken,
  setBotMessage,
  setCertificateSessionId,
  setMessages,
  setProductSessionId,
  setScenarioSessionId,
  setServerStatus,
} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {
  AvatarSessionState,
  useAvatarLifecycleMachine,
} from "@hooks/HeygenAvatar/useAvatarLifecycleMachine";
import {useAvatarMedia} from "@hooks/HeygenAvatar/useAvatarMedia";
import {useAvatarTransport} from "@hooks/HeygenAvatar/useAvatarTransport";
import {
  LIVEAVATAR_KEEP_ALIVE_INTERVAL_MS,
  LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE,
  LIVEAVATAR_SESSION_ENDED_ERROR_CODE,
  LIVEAVATAR_SESSION_IDLE_TIMEOUT_ERROR_CODE,
  LIVEAVATAR_SESSION_MAX_DURATION_ERROR_CODE,
  LIVEAVATAR_START_PAYLOAD_ERROR_CODE,
  LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
  LIVEAVATAR_TOKEN_REQUEST_ERROR_CODE,
  LiveAvatarTokenError,
  isUnexpectedLiveAvatarSessionEndReason,
  logLiveAvatarStream,
  normalizeLiveAvatarStartResponse,
  parseLiveAvatarSessionEndReason,
  postLiveAvatarSessionRequest,
} from "@hooks/HeygenAvatar/utils";
import {AvatarErrorPopup} from "@popups/simulationPopups/AvatarErrorPopup";
import {unwrapResult} from "@reduxjs/toolkit";
import {Room, RoomEvent} from "livekit-client";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector, useStore} from "react-redux";
import {v4 as uuidv4} from "uuid";

const shouldLogRoomEvent = (eventType, topic) => {
  if (!eventType || topic !== "agent-response") return false;
  if (eventType === "avatar.transcription.chunk") return false;
  return true;
};

/** Upper bound for max_session_duration / activity_idle_timeout (LiveAvatar plan: max 20 min / session). */
const LIVEAVATAR_MAX_SESSION_DURATION_CAP_SEC = 20 * 60;

export const useHeygenAvatar = (
  openPopup = null,
  isSessionInitialized = false,
  hasAvatar = false,
  volume = 1,
) => {
  const dispatch = useDispatch();
  const store = useStore();
  const {t} = useTranslation();

  const {language} = useSelector((state) => state.common.settings);
  const {
    avatarId,
    voiceId,
    product_session_id,
    messages,
    proxyList,
    serverStatus,
  } = useSelector((state) => state.ml.chat);

  const [sessionEndReason, setSessionEndReason] = useState(null);
  const sessionEndReasonRef = useRef(null);

  const {
    avatarSessionStateRef,
    avatarSessionState,
    setAvatarSessionState,
    avatarState,
    setAvatarState,
    activityState,
    setActivityState,
    messageState,
    setMessageState,
    silenceState,
    setSilenceState,
  } = useAvatarLifecycleMachine();

  const logAvatarDebug = useCallback((...args) => {
    if (process.env.NODE_ENV === "production") return;
    const firstArg = String(args?.[0] || "").toLowerCase();
    const isAllowedLog =
      firstArg.includes("connect") ||
      firstArg.includes("reconnect") ||
      firstArg.includes("close") ||
      firstArg.includes("event");
    if (!isAllowedLog) return;
    console.log("[LiveAvatar]", ...args);
  }, []);

  const startMessages = useMemo(() => [t("page.chat.start_message_1")], [t]);

  const liveAvatarLanguage = useMemo(() => {
    const normalized = String(language || "en").toLowerCase();
    const [primaryTag] = normalized.split("-");
    const supportedLanguages = new Set(["en", "ru", "de", "es", "fr"]);
    return supportedLanguages.has(primaryTag) ? primaryTag : "en";
  }, [language]);

  const liveAvatarPersonaConfig = useMemo(() => {
    const env = import.meta?.env || {};
    const contextId =
      env[`VITE_LIVEAVATAR_CONTEXT_ID_${liveAvatarLanguage.toUpperCase()}`] ||
      env.VITE_LIVEAVATAR_CONTEXT_ID ||
      null;

    const persona = {};
    if (voiceId) {
      persona.voice_id = voiceId;
    } else {
      persona.language = liveAvatarLanguage;
    }
    if (contextId) {
      persona.context_id = contextId;
    }
    return persona;
  }, [liveAvatarLanguage, voiceId]);

  const liveAvatarInteractivityType = useMemo(() => {
    const env = import.meta?.env || {};
    const value = String(env.VITE_LIVEAVATAR_INTERACTIVITY_TYPE || "").trim();
    return value ? value.toUpperCase() : null;
  }, []);

  const keepAliveIntervalMs = useMemo(() => {
    const env = import.meta?.env || {};
    const parsed = Number(env.VITE_LIVEAVATAR_KEEP_ALIVE_INTERVAL_MS);
    return Number.isFinite(parsed) && parsed >= 30_000
      ? parsed
      : LIVEAVATAR_KEEP_ALIVE_INTERVAL_MS;
  }, []);

  const isLegacyIosSafari = useMemo(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }
    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    const versionMatch = ua.match(/OS (\d+)_/);
    const iosMajor = versionMatch ? Number(versionMatch[1]) : null;
    return isIos && isSafari && iosMajor !== null && iosMajor <= 15;
  }, []);

  const lastBotPayload = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (
        m?.sender === "bot" &&
        m?.messageId &&
        String(m?.text || "").trim()
      ) {
        return {messageId: m.messageId, text: m.text};
      }
    }
    return null;
  }, [messages]);

  const avatar = useRef(null);
  const roomRef = useRef(null);
  const mediaStream = useRef(null);
  const remoteStreamRef = useRef(new MediaStream());
  const remoteAudioStreamRef = useRef(new MediaStream());
  const remoteAudioElementRef = useRef(null);
  const microphonePermissionGrantedRef = useRef(false);
  const isStoppingRef = useRef(false);
  const speakQueueRef = useRef(Promise.resolve());
  const lastSpokenMessageIdRef = useRef(null);
  const streamLogAttachedTrackIdsRef = useRef(new Set());
  const lastActiveSpeakersKeyRef = useRef("");
  const sessionStartedAtRef = useRef(null);
  const lastActivityAtRef = useRef(null);
  const lastActivityTypeRef = useRef(null);
  const reconnectStartedAtRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  const keepAliveInFlightRef = useRef(false);
  const hasHandledSessionEndRef = useRef(false);
  const mlLoadingPulseSentRef = useRef(false);
  /** Один setState + один play() после пачки треков в одном тике (без «emptied»/interrupt). */
  const streamStateFlushScheduledRef = useRef(false);
  const remoteAudioPlayScheduledRef = useRef(false);

  useEffect(() => {
    sessionEndReasonRef.current = sessionEndReason;
  }, [sessionEndReason]);

  const markActivity = useCallback((type) => {
    lastActivityAtRef.current = Date.now();
    lastActivityTypeRef.current = type;
  }, []);

  const inferDisconnectCause = useCallback((reasonRaw) => {
    const now = Date.now();
    const reason = String(reasonRaw || "").toLowerCase();
    const startedAt = sessionStartedAtRef.current;
    const lastActivityAt = lastActivityAtRef.current;
    const elapsedSec =
      typeof startedAt === "number" ? Math.round((now - startedAt) / 1000) : null;
    const idleSec =
      typeof lastActivityAt === "number"
        ? Math.round((now - lastActivityAt) / 1000)
        : null;
    const maxSessionSec = Number(avatar.current?.maxSessionDurationSec) || null;
    const idleTimeoutSec = Number(avatar.current?.activityIdleTimeoutSec) || null;

    if (reason.includes("ping timeout") || reason.includes("signal client closed")) {
      return {
        cause: "ping_timeout",
        reason,
        elapsedSec,
        idleSec,
        maxSessionSec,
        idleTimeoutSec,
      };
    }
    if (
      maxSessionSec &&
      elapsedSec != null &&
      Math.abs(elapsedSec - maxSessionSec) <= 20
    ) {
      return {
        cause: "max_session_duration",
        reason,
        elapsedSec,
        idleSec,
        maxSessionSec,
        idleTimeoutSec,
      };
    }
    if (idleTimeoutSec && idleSec != null && idleSec >= idleTimeoutSec - 5) {
      return {
        cause: "idle_timeout",
        reason,
        elapsedSec,
        idleSec,
        maxSessionSec,
        idleTimeoutSec,
      };
    }
    return {
      cause: "unknown",
      reason,
      elapsedSec,
      idleSec,
      maxSessionSec,
      idleTimeoutSec,
    };
  }, []);

  const clearRemoteMedia = useCallback(() => {
    streamLogAttachedTrackIdsRef.current.clear();
    lastActiveSpeakersKeyRef.current = "";
    remoteStreamRef.current = new MediaStream();
    remoteAudioStreamRef.current = new MediaStream();
    if (remoteAudioElementRef.current) {
      remoteAudioElementRef.current.pause();
      remoteAudioElementRef.current.srcObject = null;
    }
    setAvatarState((prev) => ({...prev, stream: null}));
  }, [setAvatarState]);

  const stopKeepAliveInterval = useCallback(() => {
    if (keepAliveIntervalRef.current != null) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const sendSessionKeepAlive = useCallback(async () => {
    const snapshot = avatar.current;
    if (!snapshot?.apiUrl || !snapshot?.sessionToken) return;
    if (avatarSessionStateRef.current !== AvatarSessionState.CONNECTED) return;
    if (keepAliveInFlightRef.current) return;

    keepAliveInFlightRef.current = true;
    try {
      const response = await postLiveAvatarSessionRequest({
        apiUrl: snapshot.apiUrl,
        sessionToken: snapshot.sessionToken,
        sessionId: snapshot.sessionId,
        pathSegment: "keep-alive",
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        console.warn("[LiveAvatar] keep-alive failed", {
          status: response.status,
          body: errorText.slice(0, 200),
          sessionId: snapshot.sessionId,
        });
        return;
      }
      markActivity("keep-alive");
      logLiveAvatarStream("keep-alive ok", {sessionId: snapshot.sessionId});
    } catch (error) {
      console.warn("[LiveAvatar] keep-alive error", error);
    } finally {
      keepAliveInFlightRef.current = false;
    }
  }, [markActivity]);

  const startKeepAliveInterval = useCallback(() => {
    stopKeepAliveInterval();
    void sendSessionKeepAlive();
    keepAliveIntervalRef.current = window.setInterval(() => {
      void sendSessionKeepAlive();
    }, keepAliveIntervalMs);
  }, [keepAliveIntervalMs, sendSessionKeepAlive, stopKeepAliveInterval]);

  const releaseAvatarConnection = useCallback(
    async ({callStopApi = false} = {}) => {
      stopKeepAliveInterval();

      if (roomRef.current) {
        try {
          roomRef.current.disconnect();
        } catch (error) {
          console.warn("[LiveAvatar] room disconnect error", error);
        }
        roomRef.current = null;
      }

      clearRemoteMedia();

      if (callStopApi) {
        const snapshot = avatar.current;
        if (snapshot?.apiUrl && snapshot?.sessionToken) {
          await postLiveAvatarSessionRequest({
            apiUrl: snapshot.apiUrl,
            sessionToken: snapshot.sessionToken,
            sessionId: snapshot.sessionId,
            pathSegment: "stop",
          }).catch((error) => {
            console.error("Error stopping LiveAvatar session:", error);
          });
        }
      }

      avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
      setAvatarSessionState(AvatarSessionState.INACTIVE);
      reconnectStartedAtRef.current = null;
    },
    [
      avatarSessionStateRef,
      clearRemoteMedia,
      setAvatarSessionState,
      stopKeepAliveInterval,
    ],
  );

  const {enableTransportBoundary, disableTransportBoundary} =
    useAvatarTransport({
      dispatch,
      proxyList,
    });

  const handleUnexpectedSessionEnd = useCallback(
    async (endReason) => {
      if (isStoppingRef.current || hasHandledSessionEndRef.current) return;
      hasHandledSessionEndRef.current = true;

      const diagnosis = inferDisconnectCause(endReason || "session.stopped");
      console.warn("[LiveAvatar] unexpected session.stopped", {
        endReason,
        ...diagnosis,
        lastActivityType: lastActivityTypeRef.current,
        sessionId: avatar.current?.sessionId || null,
      });
      logLiveAvatarStream("session.stopped (unexpected)", {
        endReason,
        diagnosis,
      });

      setSessionEndReason(endReason || "UNKNOWN");
      await releaseAvatarConnection({callStopApi: false});
      avatar.current = null;
      disableTransportBoundary();
    },
    [disableTransportBoundary, inferDisconnectCause, releaseAvatarConnection],
  );

  useEffect(() => {
    if (typeof window === "undefined" || remoteAudioElementRef.current) return;
    const audio = new Audio();
    audio.autoplay = true;
    audio.playsInline = true;
    audio.preload = "auto";
    audio.volume = volume;
    remoteAudioElementRef.current = audio;

    return () => {
      if (remoteAudioElementRef.current) {
        remoteAudioElementRef.current.pause();
        remoteAudioElementRef.current.srcObject = null;
      }
      remoteAudioElementRef.current = null;
    };
  }, []);

  useAvatarMedia({
    avatarSessionState,
    AvatarSessionState,
    avatarState,
    mediaStream,
    remoteAudioElementRef,
    volume,
    logAvatarDebug,
    logLiveAvatarStream,
  });

  const fetchAccessToken = useCallback(
    async (sessionConfig) => {
      const resultAction = await dispatch(
        getAvatarToken({
          session: sessionConfig,
        }),
      );

      if (!getAvatarToken.fulfilled.match(resultAction)) {
        throw new Error("Unable to retrieve LiveAvatar session token");
      }

      const payload = unwrapResult(resultAction);
      if (!payload) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
          "LiveAvatar token endpoint returned empty response",
        );
      }

      if (payload?.error) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_REQUEST_ERROR_CODE,
          "LiveAvatar token endpoint returned backend error",
          {backendError: payload.error},
        );
      }

      const data = payload?.data || payload;
      const sessionToken = data?.session_token || payload?.session_token;
      const sessionId = data?.session_id || payload?.session_id || null;
      const url = payload?.url || data?.url;

      if (!sessionToken) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
          "Session token does not exist",
          {availableKeys: Object.keys(data || {})},
        );
      }

      if (!url) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
          "LiveAvatar API URL does not exist",
          {availableKeys: Object.keys(payload || {})},
        );
      }

      return {
        sessionToken,
        sessionId,
        url: url.replace(/\/$/, ""),
      };
    },
    [dispatch],
  );

  const initAvatar = useCallback(({sessionToken, sessionId, url}) => {
    avatar.current = {
      sessionToken,
      sessionId,
      apiUrl: url,
    };
    return avatar.current;
  }, []);

  const publishCommandEvent = useCallback(async (eventType, eventData = {}) => {
    if (!roomRef.current || !avatar.current) {
      throw new Error("LiveAvatar room is not initialized");
    }

    const payload = {
      event_type: eventType,
      session_id: avatar.current.sessionId,
      ...eventData,
    };
    const encoded = new TextEncoder().encode(JSON.stringify(payload));

    await roomRef.current.localParticipant.publishData(encoded, {
      reliable: true,
      topic: "agent-control",
    });
    markActivity(`command:${eventType}`);
  }, [markActivity]);

  const startAvatar = useCallback(async () => {
    if (avatarSessionStateRef.current !== AvatarSessionState.INACTIVE) {
      throw new Error("There is already an active session");
    }
    if (!avatar.current?.sessionToken || !avatar.current?.apiUrl) {
      throw new Error("Avatar is not initialized");
    }

    avatarSessionStateRef.current = AvatarSessionState.CONNECTING;
    setAvatarSessionState(AvatarSessionState.CONNECTING);

    try {
      streamStateFlushScheduledRef.current = false;
      remoteAudioPlayScheduledRef.current = false;
      remoteStreamRef.current = new MediaStream();
      remoteAudioStreamRef.current = new MediaStream();

      const attachRemoteTrack = (
        track,
        publication = null,
        participant = null,
      ) => {
        const trackKind = String(track?.kind || "").toLowerCase();
        if (trackKind !== "video" && trackKind !== "audio") return;

        const mediaTrack = track?.mediaStreamTrack;
        if (!mediaTrack) return;

        logLiveAvatarStream("TrackSubscribed → attachRemoteTrack", {
          kind: trackKind,
          trackSid: track?.sid || null,
          mediaTrackId: mediaTrack.id,
          mediaTrackLabel: mediaTrack.label,
          muted: mediaTrack.muted,
          readyState: mediaTrack.readyState,
          enabled: mediaTrack.enabled,
          publicationSid: publication?.trackSid || publication?.trackName || null,
          participantIdentity: participant?.identity || null,
          videoDims:
            trackKind === "video" ? track?.dimensions || null : undefined,
          audioSettings:
            trackKind === "audio" ? mediaTrack.getSettings?.() || {} : undefined,
        });

        if (!streamLogAttachedTrackIdsRef.current.has(mediaTrack.id)) {
          streamLogAttachedTrackIdsRef.current.add(mediaTrack.id);
          mediaTrack.addEventListener("mute", () => {
            logLiveAvatarStream("MediaStreamTrack mute", {
              kind: mediaTrack.kind,
              id: mediaTrack.id,
            });
          });
          mediaTrack.addEventListener("unmute", () => {
            logLiveAvatarStream("MediaStreamTrack unmute", {
              kind: mediaTrack.kind,
              id: mediaTrack.id,
            });
          });
          mediaTrack.addEventListener("ended", () => {
            logLiveAvatarStream("MediaStreamTrack ended", {
              kind: mediaTrack.kind,
              id: mediaTrack.id,
            });
            streamLogAttachedTrackIdsRef.current.delete(mediaTrack.id);
          });
        }

        const hasTrack = remoteStreamRef.current
          .getTracks()
          .some((existingTrack) => existingTrack.id === mediaTrack.id);

        if (!hasTrack) {
          remoteStreamRef.current.addTrack(mediaTrack);
          if (!streamStateFlushScheduledRef.current) {
            streamStateFlushScheduledRef.current = true;
            queueMicrotask(() => {
              streamStateFlushScheduledRef.current = false;
              setAvatarState((prev) => ({
                ...prev,
                stream: remoteStreamRef.current,
              }));
            });
          }
          logLiveAvatarStream("remote composite MediaStream updated", {
            videoTracks: remoteStreamRef.current.getVideoTracks().length,
            audioTracks: remoteStreamRef.current.getAudioTracks().length,
          });
        }

        if (trackKind === "audio") {
          const hasAudioTrack = remoteAudioStreamRef.current
            .getTracks()
            .some((existingTrack) => existingTrack.id === mediaTrack.id);

          if (!hasAudioTrack) {
            remoteAudioStreamRef.current.addTrack(mediaTrack);
            logLiveAvatarStream("remoteAudioStream track added", {
              audioTracks: remoteAudioStreamRef.current.getAudioTracks().length,
            });
          }

          if (remoteAudioElementRef.current) {
            /** Один раз после пачки треков: иначе два подряд `srcObject=` рвут play(). */
            if (!remoteAudioPlayScheduledRef.current) {
              remoteAudioPlayScheduledRef.current = true;
              queueMicrotask(() => {
                remoteAudioPlayScheduledRef.current = false;
                const el = remoteAudioElementRef.current;
                if (!el) return;
                el.srcObject = remoteAudioStreamRef.current;
                /** Macrotask после React/LiveKit microtasks — иначе play() часто «interrupted by new load». */
                setTimeout(() => {
                  const aud = remoteAudioElementRef.current;
                  if (!aud) return;
                  aud.play().catch((e) => {
                    const msg = e?.message || String(e);
                    if (msg.includes("interrupted")) return;
                    logLiveAvatarStream("remoteAudioElement.play() rejected", {
                      message: msg,
                    });
                  });
                }, 0);
              });
            }
          }
        }
      };

      const startResponse = await fetch(
        `${avatar.current.apiUrl}/sessions/start`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${avatar.current.sessionToken}`,
            Accept: "application/json",
          },
        },
      );

      if (!startResponse.ok) {
        const errorText = await startResponse.text();
        throw new Error(
          `LiveAvatar start failed: ${startResponse.status} ${errorText}`,
        );
      }

      const rawStartPayload = await startResponse.json();
      const normalizedStart = normalizeLiveAvatarStartResponse(rawStartPayload);
      const sessionDurationLimitSec =
        Number(avatar.current?.maxSessionDurationSec) || 0;
      const sessionDurationLimitMin =
        sessionDurationLimitSec > 0
          ? Number((sessionDurationLimitSec / 60).toFixed(2))
          : 0;
      const activityIdleTimeoutSec =
        Number(avatar.current?.activityIdleTimeoutSec) || 0;
      const activityIdleTimeoutMin = Number(
        (activityIdleTimeoutSec / 60).toFixed(2),
      );

      console.log(
        `[LiveAvatar] Session limits: session_duration = ${sessionDurationLimitSec}sec/${sessionDurationLimitMin}min, activity_iddle = ${activityIdleTimeoutSec}sec/${activityIdleTimeoutMin}min`,
      );

      if (normalizedStart.sessionId) {
        avatar.current.sessionId = normalizedStart.sessionId;
      }
      sessionStartedAtRef.current = Date.now();
      markActivity("session_started");

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        attachRemoteTrack(track, publication, participant);
      });
      room.on(
        RoomEvent.TrackUnsubscribed,
        (track, publication, participant) => {
          const mediaTrack = track.mediaStreamTrack;
          logLiveAvatarStream("TrackUnsubscribed", {
            kind: track?.kind,
            trackSid: track?.sid || null,
            mediaTrackId: mediaTrack?.id || null,
            publicationSid: publication?.trackSid || null,
            participantIdentity: participant?.identity || null,
          });
          if (!mediaTrack) return;
          streamLogAttachedTrackIdsRef.current.delete(mediaTrack.id);
          remoteStreamRef.current.removeTrack(mediaTrack);
          remoteAudioStreamRef.current.removeTrack(mediaTrack);

          if (
            remoteAudioElementRef.current &&
            remoteAudioStreamRef.current.getTracks().length === 0
          ) {
            remoteAudioElementRef.current.pause();
            remoteAudioElementRef.current.srcObject = null;
            logLiveAvatarStream("remoteAudioElement cleared (no audio tracks)");
          }

          setAvatarState((prev) => ({
            ...prev,
            stream:
              remoteStreamRef.current.getTracks().length > 0
                ? remoteStreamRef.current
                : null,
          }));
        },
      );

      room.on(RoomEvent.TrackMuted, (publication, participant) => {
        logLiveAvatarStream("TrackMuted", {
          kind: publication?.kind,
          trackSid: publication?.trackSid,
          participantIdentity: participant?.identity || null,
        });
      });
      room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
        logLiveAvatarStream("TrackUnmuted", {
          kind: publication?.kind,
          trackSid: publication?.trackSid,
          participantIdentity: participant?.identity || null,
        });
      });

      room.on(
        RoomEvent.TrackStreamStateChanged,
        (publication, streamState, participant) => {
          logLiveAvatarStream("TrackStreamStateChanged (SFU pause/resume)", {
            kind: publication?.kind,
            trackSid: publication?.trackSid,
            streamState: String(streamState),
            participantIdentity: participant?.identity || null,
          });
        },
      );

      room.on(
        RoomEvent.TrackSubscriptionStatusChanged,
        (publication, status, participant) => {
          logLiveAvatarStream("TrackSubscriptionStatusChanged", {
            kind: publication?.kind,
            trackSid: publication?.trackSid,
            status: String(status),
            participantIdentity: participant?.identity || null,
          });
        },
      );

      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        logLiveAvatarStream("AudioPlaybackStatusChanged", {
          canPlaybackAudio: room.canPlaybackAudio,
        });
      });

      room.on(RoomEvent.VideoPlaybackStatusChanged, () => {
        logLiveAvatarStream("VideoPlaybackStatusChanged", {});
      });

      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        const key = (speakers || [])
          .map((p) => p?.identity || "?")
          .join("|");
        if (key === lastActiveSpeakersKeyRef.current) return;
        lastActiveSpeakersKeyRef.current = key;
        logLiveAvatarStream("ActiveSpeakersChanged", {
          identities: (speakers || []).map((p) => p?.identity || null),
        });
      });

      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        logLiveAvatarStream("Room ConnectionStateChanged", {
          state: String(state),
        });
        setActivityState((prev) => ({
          ...prev,
          connectionState: String(state || "unknown"),
        }));
      });

      room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
        try {
          const decoded = new TextDecoder().decode(payload);
          const eventData = JSON.parse(decoded);
          const eventType = eventData?.event_type || eventData?.type || null;
          const incomingSessionId =
            eventData?.session_id || eventData?.sessionId || null;

          if (incomingSessionId && avatar.current) {
            avatar.current.sessionId = incomingSessionId;
          }

          if (shouldLogRoomEvent(eventType, topic)) {
            logAvatarDebug(`room data event: type "${eventType}"`);
          }

          if (topic !== "agent-response") return;

          switch (eventType) {
            case "avatar.speak_started":
              logLiveAvatarStream("agent-response avatar.speak_started");
              markActivity("avatar.speak_started");
              setActivityState((prev) => ({...prev, isAvatarTalking: true}));
              break;
            case "avatar.speak_ended":
              logLiveAvatarStream("agent-response avatar.speak_ended");
              markActivity("avatar.speak_ended");
              setActivityState((prev) => ({...prev, isAvatarTalking: false}));
              setSilenceState((prev) => ({...prev, stopVoice: true}));
              break;
            case "user.speak_started":
              markActivity("user.speak_started");
              setActivityState((prev) => ({...prev, isUserTalking: true}));
              break;
            case "user.speak_ended":
              markActivity("user.speak_ended");
              setActivityState((prev) => ({...prev, isUserTalking: false}));
              break;
            case "session.stopped": {
              const endReason = parseLiveAvatarSessionEndReason(eventData);
              markActivity(`session.stopped:${endReason || "unknown"}`);
              logLiveAvatarStream("agent-response session.stopped", {
                endReason,
              });

              if (isStoppingRef.current) {
                avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
                setAvatarSessionState(AvatarSessionState.INACTIVE);
                break;
              }

              if (isUnexpectedLiveAvatarSessionEndReason(endReason)) {
                void handleUnexpectedSessionEnd(endReason);
              } else {
                hasHandledSessionEndRef.current = true;
                void releaseAvatarConnection({callStopApi: false}).then(() => {
                  avatar.current = null;
                  disableTransportBoundary();
                });
              }
              break;
            }
            default:
              break;
          }
        } catch (error) {
          console.error("Error parsing LiveAvatar event:", error);
        }
      });

      room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        logLiveAvatarStream("ConnectionQualityChanged", {
          quality: String(quality || "UNKNOWN"),
          participantIdentity: participant?.identity || null,
          isLocal: Boolean(participant?.isLocal),
        });
        setActivityState((prev) => ({
          ...prev,
          connectionQuality: String(quality || "UNKNOWN"),
        }));
      });
      room.on(RoomEvent.Reconnecting, () => {
        logAvatarDebug("room reconnecting");
        reconnectStartedAtRef.current = Date.now();
        console.warn("[LiveAvatar] reconnecting", {
          sessionElapsedSec:
            sessionStartedAtRef.current != null
              ? Math.round((Date.now() - sessionStartedAtRef.current) / 1000)
              : null,
          lastActivityType: lastActivityTypeRef.current,
          idleSec:
            lastActivityAtRef.current != null
              ? Math.round((Date.now() - lastActivityAtRef.current) / 1000)
              : null,
        });
        avatarSessionStateRef.current = AvatarSessionState.CONNECTING;
        setAvatarSessionState(AvatarSessionState.CONNECTING);
      });
      room.on(RoomEvent.Reconnected, () => {
        logAvatarDebug("room reconnected");
        console.info("[LiveAvatar] reconnected", {
          reconnectDurationSec:
            reconnectStartedAtRef.current != null
              ? Math.round((Date.now() - reconnectStartedAtRef.current) / 1000)
              : null,
        });
        reconnectStartedAtRef.current = null;
        markActivity("room.reconnected");
        avatarSessionStateRef.current = AvatarSessionState.CONNECTED;
        setAvatarSessionState(AvatarSessionState.CONNECTED);
      });
      room.on(RoomEvent.Disconnected, (reason) => {
        logAvatarDebug("room disconnected");
        stopKeepAliveInterval();

        if (hasHandledSessionEndRef.current) {
          hasHandledSessionEndRef.current = false;
          logLiveAvatarStream("Room Disconnected (after session.stopped)", {
            reason: reason !== undefined ? String(reason) : null,
          });
          return;
        }

        const diagnosis = inferDisconnectCause(reason);
        console.warn("[LiveAvatar] disconnect diagnosis", {
          ...diagnosis,
          lastActivityType: lastActivityTypeRef.current,
          sessionId: avatar.current?.sessionId || null,
          isStopping: isStoppingRef.current,
          reconnectDurationSec:
            reconnectStartedAtRef.current != null
              ? Math.round((Date.now() - reconnectStartedAtRef.current) / 1000)
              : null,
        });
        logLiveAvatarStream("Room Disconnected", {
          reason: reason !== undefined ? String(reason) : null,
        });
        clearRemoteMedia();
        avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
        setAvatarSessionState(AvatarSessionState.INACTIVE);
        reconnectStartedAtRef.current = null;

        if (
          !isStoppingRef.current &&
          diagnosis.cause === "idle_timeout" &&
          !sessionEndReasonRef.current
        ) {
          setSessionEndReason("IDLE_TIMEOUT");
        }
      });

      await room.connect(
        normalizedStart.livekitUrl,
        normalizedStart.livekitToken,
      );
      logAvatarDebug(
        `room connected: pID "${room.localParticipant?.sid || "unknown"}"`,
      );

      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications?.forEach((publication) => {
          if (publication?.isSubscribed && publication?.track) {
            attachRemoteTrack(
              publication.track,
              publication,
              participant,
            );
          }
        });
      });

      roomRef.current = room;
      avatarSessionStateRef.current = AvatarSessionState.CONNECTED;
      setAvatarSessionState(AvatarSessionState.CONNECTED);
      setAvatarState((prev) => ({
        ...prev,
        avatarData: normalizedStart.startData,
      }));
      hasHandledSessionEndRef.current = false;
      startKeepAliveInterval();
    } catch (error) {
      stopKeepAliveInterval();
      avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
      setAvatarSessionState(AvatarSessionState.INACTIVE);
      throw error;
    }
  }, [
    avatarSessionStateRef,
    clearRemoteMedia,
    disableTransportBoundary,
    handleUnexpectedSessionEnd,
    inferDisconnectCause,
    logAvatarDebug,
    markActivity,
    releaseAvatarConnection,
    setActivityState,
    setAvatarSessionState,
    setAvatarState,
    setSilenceState,
    startKeepAliveInterval,
    stopKeepAliveInterval,
  ]);

  const stopAvatar = useCallback(async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    try {
      await releaseAvatarConnection({callStopApi: true});

      avatar.current = null;
      speakQueueRef.current = Promise.resolve();
      lastSpokenMessageIdRef.current = null;
      sessionStartedAtRef.current = null;
      lastActivityAtRef.current = null;
      lastActivityTypeRef.current = null;
      hasHandledSessionEndRef.current = false;
      setAvatarState({stream: null, avatarData: null});
      setActivityState({
        isAvatarTalking: false,
        isUserTalking: false,
        isListening: false,
        connectionState: "disconnected",
        connectionQuality: "UNKNOWN",
      });
      setMessageState({
        lastSpokenMessageId: null,
        firstMessageSent: false,
      });
      setSilenceState({
        silence: false,
        countdown: 115,
        stopVoice: true,
      });
    } catch (error) {
      console.error("Error stopping avatar:", error);
    } finally {
      isStoppingRef.current = false;
      disableTransportBoundary();
    }
  }, [
    releaseAvatarConnection,
    setActivityState,
    setAvatarState,
    setMessageState,
    setSilenceState,
  ]);

  const clearAllAvatarState = useCallback(async () => {
    try {
      await stopAvatar();
      dispatch(clearChatState());
      dispatch(setProductSessionId(null));
      dispatch(setScenarioSessionId(null));
      dispatch(setCertificateSessionId(null));
      dispatch(setServerStatus(""));
    } catch (error) {
      console.error("Error clearing avatar state:", error);
    }
  }, [dispatch, stopAvatar]);

  const ensureMicrophonePermission = useCallback(async () => {
    if (microphonePermissionGrantedRef.current) return;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      stream.getTracks().forEach((track) => track.stop());
      microphonePermissionGrantedRef.current = true;
    } catch (error) {
      const microphoneError = new Error("Microphone permission is required");
      microphoneError.code = LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE;
      microphoneError.cause = error;
      throw microphoneError;
    }
  }, []);

  const enqueueSpeakTask = useCallback((task) => {
    const run = speakQueueRef.current
      .then(() => task())
      .catch((error) => {
        console.error("Error speaking:", error);
      });
    speakQueueRef.current = run;
    return run;
  }, []);

  const speakBotMessageById = useCallback(
    (messageId, text) =>
      enqueueSpeakTask(async () => {
        if (!roomRef.current || !avatar.current) return;
        if (avatarSessionStateRef.current !== AvatarSessionState.CONNECTED) {
          return;
        }
        const trimmed = String(text || "").trim();
        if (!trimmed || !messageId) return;
        if (lastSpokenMessageIdRef.current === messageId) return;

        lastSpokenMessageIdRef.current = messageId;
        setMessageState((prev) => ({
          ...prev,
          lastSpokenMessageId: messageId,
        }));

        setSilenceState((prev) => ({
          ...prev,
          countdown: 115,
          silence: false,
          stopVoice: false,
        }));

        await publishCommandEvent("avatar.speak_text", {text: trimmed});
      }),
    [
      enqueueSpeakTask,
      publishCommandEvent,
      setMessageState,
      setSilenceState,
    ],
  );

  const speakText = useCallback(
    (text, isSilenceMessage = false) => {
      if (!isSilenceMessage) {
        return Promise.resolve();
      }
      return enqueueSpeakTask(async () => {
        if (!roomRef.current || !avatar.current) return;
        if (avatarSessionStateRef.current !== AvatarSessionState.CONNECTED) {
          return;
        }
        const trimmed = String(text || "").trim();
        if (!trimmed) return;

        setSilenceState((prev) => ({
          ...prev,
          countdown: 115,
          silence: false,
          stopVoice: false,
        }));

        await publishCommandEvent("avatar.speak_text", {text: trimmed});
      });
    },
    [enqueueSpeakTask, publishCommandEvent, setSilenceState],
  );

  const interruptAvatar = useCallback(async () => {
    if (!roomRef.current) return;
    try {
      await publishCommandEvent("avatar.interrupt");
    } catch (error) {
      console.error("Error interrupting avatar:", error);
    }
  }, [publishCommandEvent]);

  const firstProductMessage = useCallback(async () => {
    if (
      product_session_id &&
      messages.length === 0 &&
      !messageState.firstMessageSent
    ) {
      const text =
        startMessages[Math.floor(Math.random() * startMessages.length)];
      const startMessage = {messageId: uuidv4(), text, sender: "bot"};

      await dispatch(setBotMessage(text));
      await dispatch(setMessages(startMessage));
      setMessageState((prev) => ({...prev, firstMessageSent: true}));
    }
  }, [
    dispatch,
    messageState.firstMessageSent,
    messages.length,
    product_session_id,
    setMessageState,
    startMessages,
  ]);

  const initAvatarWithToken = useCallback(async () => {
    await ensureMicrophonePermission();
    await enableTransportBoundary();
    setSessionEndReason(null);
    hasHandledSessionEndRef.current = false;

    try {
      const sec = store.getState().ml.chat.sessionDurationSec;
      const sessionMs =
        Number.isFinite(sec) && sec > 0 ? sec * 1000 : null;
      const fallbackIdleTimeoutSeconds = LIVEAVATAR_MAX_SESSION_DURATION_CAP_SEC;
      const idleTimeoutSecondsFromRemaining = Number.isFinite(sessionMs)
        ? Math.floor((sessionMs + 5 * 60 * 1000) / 1000)
        : fallbackIdleTimeoutSeconds;
      const activityIdleTimeout = Math.max(
        30,
        Math.min(
          LIVEAVATAR_MAX_SESSION_DURATION_CAP_SEC,
          idleTimeoutSecondsFromRemaining,
        ),
      );

      const sessionConfig = {
        mode: "FULL",
        avatar_id: avatarId,
        quality: isLegacyIosSafari ? "medium" : "high",
        avatar_persona: liveAvatarPersonaConfig,
        activity_idle_timeout: activityIdleTimeout,
        max_session_duration: activityIdleTimeout,
        ...(liveAvatarInteractivityType
          ? {interactivity_type: liveAvatarInteractivityType}
          : {}),
      };

      const {sessionToken, sessionId, url} =
        await fetchAccessToken(sessionConfig);
      initAvatar({sessionToken, sessionId, url});
      if (avatar.current) {
        avatar.current.activityIdleTimeoutSec = activityIdleTimeout;
        avatar.current.maxSessionDurationSec = activityIdleTimeout;
      }
      await startAvatar();
    } catch (error) {
      disableTransportBoundary();
      throw error;
    }
  }, [
    avatarId,
    disableTransportBoundary,
    enableTransportBoundary,
    ensureMicrophonePermission,
    fetchAccessToken,
    initAvatar,
    isLegacyIosSafari,
    liveAvatarInteractivityType,
    liveAvatarPersonaConfig,
    startAvatar,
    store,
  ]);

  const showAvatarError = useCallback(
    (onTryAgain, onExit, error = null) => {
      const detailedMessage =
        error?.code === LIVEAVATAR_START_PAYLOAD_ERROR_CODE
          ? t("popup.avatar_error.invalid_start_payload")
          : error?.code === LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE
            ? t("popup.avatar_error.token_empty_response")
            : error?.code === LIVEAVATAR_TOKEN_REQUEST_ERROR_CODE
              ? t("popup.avatar_error.token_request_error")
            : error?.code === LIVEAVATAR_SESSION_IDLE_TIMEOUT_ERROR_CODE
              ? t("popup.avatar_error.session_idle_timeout")
            : error?.code === LIVEAVATAR_SESSION_MAX_DURATION_ERROR_CODE
              ? t("popup.avatar_error.session_max_duration")
            : error?.code === LIVEAVATAR_SESSION_ENDED_ERROR_CODE
              ? t("popup.avatar_error.session_ended")
              : "";

      if (!openPopup) return;

      openPopup(
        <AvatarErrorPopup
          onClickTryAgain={onTryAgain}
          onClickExit={onExit}
          detailedMessage={detailedMessage}
        />,
        {closeButton: false},
      );
    },
    [openPopup, t],
  );

  useEffect(() => {
    if (serverStatus !== "loading") {
      mlLoadingPulseSentRef.current = false;
      return;
    }
    if (avatarSessionState !== AvatarSessionState.CONNECTED) return;
    if (mlLoadingPulseSentRef.current) return;
    if (!roomRef.current || !avatar.current) return;

    mlLoadingPulseSentRef.current = true;
    void publishCommandEvent("avatar.start_listening").catch((error) => {
      console.warn("[LiveAvatar] ML loading pulse failed", error);
      mlLoadingPulseSentRef.current = false;
    });
  }, [avatarSessionState, publishCommandEvent, serverStatus]);

  useEffect(() => {
    if (!lastBotPayload) return;
    if (avatarSessionState !== AvatarSessionState.CONNECTED) return;
    void speakBotMessageById(lastBotPayload.messageId, lastBotPayload.text);
  }, [
    avatarSessionState,
    lastBotPayload?.messageId,
    lastBotPayload?.text,
    speakBotMessageById,
  ]);

  useEffect(() => {
    if (
      product_session_id &&
      !messageState.firstMessageSent &&
      isSessionInitialized &&
      (!hasAvatar || avatarSessionState === AvatarSessionState.CONNECTED)
    ) {
      firstProductMessage();
    }
  }, [
    avatarSessionState,
    firstProductMessage,
    hasAvatar,
    isSessionInitialized,
    messageState.firstMessageSent,
    product_session_id,
  ]);

  useEffect(() => {
    if (silenceState.countdown <= 0) {
      setSilenceState((prev) => ({...prev, silence: true}));
      return;
    }

    if (avatarSessionState !== AvatarSessionState.CONNECTED) return;

    const timer = setInterval(() => {
      setSilenceState((prev) => ({...prev, countdown: prev.countdown - 1}));
    }, 1000);

    return () => clearInterval(timer);
  }, [avatarSessionState, setSilenceState, silenceState.countdown]);

  useEffect(() => {
    if (
      avatarSessionState === AvatarSessionState.CONNECTED &&
      silenceState.silence
    ) {
      speakText(t("page.chat.silence_message"), true);
    } else if (silenceState.silence) {
      setSilenceState((prev) => ({
        ...prev,
        countdown: 115,
        silence: false,
      }));
    }
  }, [avatarSessionState, setSilenceState, silenceState.silence, speakText, t]);

  useEffect(() => {
    return () => {
      stopKeepAliveInterval();
      clearAllAvatarState();
    };
  }, [clearAllAvatarState, stopKeepAliveInterval]);

  return {
    avatarSessionState,
    sessionEndReason,
    mediaStream,
    AvatarSessionState,
    stream: avatarState.stream,
    avatarData: avatarState.avatarData,
    isAvatarTalking: activityState.isAvatarTalking,
    isUserTalking: activityState.isUserTalking,
    isListening: activityState.isListening,
    connectionState: activityState.connectionState,
    connectionQuality: activityState.connectionQuality,
    silence: silenceState.silence,
    countdown: silenceState.countdown,
    stopVoice: silenceState.stopVoice,
    fetchAccessToken,
    initAvatar,
    stopAvatar,
    speakText,
    interruptAvatar,
    ensureMicrophonePermission,
    initAvatarWithToken,
    firstProductMessage,
    clearAllAvatarState,
    showAvatarError,
    setAvatarSessionState,
  };
};
